import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2, AlertTriangle, Circle, RotateCcw, Sparkles, ChevronDown, XCircle, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { api, errMsg, STAGES } from "@/lib/api";
import { ETA, HELP } from "@/lib/guide";

export function RunStatus({ run }) {
  if (!run) return null;
  return (
    <div data-testid="run-status" className="text-xs text-muted-foreground flex items-center gap-2">
      {run.status === "done" ? <CheckCircle2 size={12} className="text-emerald-400" /> : <XCircle size={12} className="text-rose-400" />}
      Last AI run {run.status === "done" ? "finished" : "failed"} · {run.who} · {new Date(run.finished_at || run.created_at).toLocaleString()}
      {run.note && <span className="text-blue-300" data-testid="run-note">· {run.note}</span>}
    </div>
  );
}

const RERUN = /re-run|run (a|round|the)|collect again|widen/i;

function Card({ tone, icon, title, children, testid }) {
  const tones = {
    blue: "border-blue-500/40 bg-gradient-to-r from-blue-600/10 to-transparent",
    green: "border-emerald-500/40 bg-gradient-to-r from-emerald-600/10 to-transparent",
    amber: "border-amber-500/40 bg-gradient-to-r from-amber-500/10 to-transparent",
    red: "border-rose-500/40 bg-gradient-to-r from-rose-600/10 to-transparent",
  };
  return (
    <div data-testid={testid} className={`rounded-xl border p-5 fade-in ${tones[tone]}`}>
      <div className="flex items-start gap-4">
        <div className="mt-0.5">{icon}</div>
        <div className="flex-1 min-w-0"><div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Your next step</div>
          <div className="text-lg font-semibold font-display text-slate-50" data-testid="next-step-title">{title}</div>{children}</div>
      </div>
    </div>
  );
}

function NextStep({ job, stage, st, runHook, runs, missing, approve, goNext }) {
  const run = runHook?.run;
  const primary = runs.find((r) => !r.disabled) || runs[0];
  const startBtn = (r, label) => (
    <Button data-testid="next-step-run-button" disabled={r?.disabled} onClick={() => runHook.start(r.action, r.params ? r.params() : {})}
      className="mt-4 bg-blue-600 hover:bg-blue-500 text-white h-10 px-5"><Sparkles size={15} className="mr-2" />{label || r.label}</Button>
  );
  const nextName = STAGES[STAGES.findIndex((s) => s.id === stage) + 1]?.name;

  if (runHook?.busy) {
    return (
      <Card tone="blue" testid="next-step-card" icon={<Loader2 className="animate-spin text-blue-400" size={22} />} title="AI is working on this. Nothing for you to do yet.">
        <div className="text-sm text-slate-300 mt-1" data-testid="run-status-message">{run.message}</div>
        <Progress value={run.progress || 4} className="h-1.5 mt-3 max-w-xl" />
        <div className="text-xs text-muted-foreground mt-2">Usually takes {ETA[run.action] || "a minute"}. You can leave this page; it keeps running.</div>
      </Card>
    );
  }
  if (st.status === "approved" && !(st.stale && missing.length)) {
    return (
      <Card tone="green" testid="next-step-card" icon={<CheckCircle2 className="text-emerald-400" size={22} />} title={stage === 10 ? "All done. The resume is approved." : `This step is approved. Continue to ${nextName}.`}>
        {stage !== 10 && <Button data-testid="next-step-continue-button" onClick={goNext} className="mt-4 bg-emerald-600 hover:bg-emerald-500 text-white h-10 px-5">Go to {nextName}<ArrowRight size={15} className="ml-2" /></Button>}
      </Card>
    );
  }
  if (st.status === "failed" && primary) {
    return (
      <Card tone="red" testid="next-step-card" icon={<AlertTriangle className="text-rose-400" size={22} />} title="The AI step didn't finish. Try again.">
        <div className="text-sm text-rose-200/80 mt-1">{run?.error}</div>{startBtn(primary, `Retry: ${primary.label}`)}
      </Card>
    );
  }
  if (st.status === "pending" && primary && !primary.disabled) {
    return (
      <Card tone="blue" testid="next-step-card" icon={<Sparkles className="text-blue-400" size={22} />} title={`Start: ${primary.label}`}>
        <div className="text-sm text-slate-400 mt-1">{HELP[stage]?.purpose}</div>{startBtn(primary)}
      </Card>
    );
  }
  if (missing.length) {
    const needsRun = missing.some((m) => RERUN.test(m)) && primary;
    return (
      <Card tone="amber" testid="next-step-card" icon={<Circle className="text-amber-400" size={22} />} title={missing.length === 1 ? missing[0] : `Finish these ${missing.length} items to continue`}>
        {missing.length > 1 && <ul className="mt-2 space-y-1.5" data-testid="gate-missing-list">{missing.map((m) => (
          <li key={m} className="flex items-start gap-2 text-sm text-slate-300"><Circle size={12} className="text-amber-400 mt-1 shrink-0" />{m}</li>))}</ul>}
        {missing.length === 1 && <div data-testid="gate-missing-list" className="hidden">{missing[0]}</div>}
        {needsRun && startBtn(primary)}
      </Card>
    );
  }
  return (
    <Card tone="green" testid="next-step-card" icon={<CheckCircle2 className="text-emerald-400" size={22} />} title={stage === 10 ? "Everything is complete." : `Everything checks out. Approve to move on to ${nextName}.`}>
      {stage !== 10 && <div className="text-xs text-muted-foreground mt-1">{[0, 1, 2, 3, 4, 5, 6, 7, 8].includes(stage) ? "The next AI step starts automatically." : ""}</div>}
      {stage !== 10 && <Button data-testid="stage-approve-continue-button" onClick={approve} className="mt-4 bg-emerald-600 hover:bg-emerald-500 text-white h-10 px-5">
        <CheckCircle2 size={15} className="mr-2" />Approve and continue</Button>}
    </Card>
  );
}

export default function StageShell({ job, stage, refresh, runHook, runs = [], children, extra }) {
  const [missing, setMissing] = useState(null);
  const [help, setHelp] = useState(false);
  const st = job.stages[String(stage)] || {};
  const meta = STAGES.find((s) => s.id === stage);
  const idx = STAGES.findIndex((s) => s.id === stage);

  useEffect(() => {
    api.get(`/jobs/${job.id}/gate/${stage}`).then(({ data }) => setMissing(data.missing));
  }, [job, stage, runHook?.busy]);

  const approve = async () => {
    try { await api.post(`/jobs/${job.id}/stages/${stage}/approve`); toast.success(`${meta.name} approved`); refresh(true); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const reopen = async () => { await api.post(`/jobs/${job.id}/stages/${stage}/reopen`); refresh(); };
  const hasOutput = st.last_run_id && !runHook?.busy && !(job.client_approval && stage >= 4);
  const locked = idx > STAGES.findIndex((s) => s.id === job.current_stage);
  const curName = STAGES.find((s) => s.id === job.current_stage)?.name;
  if (locked) {
    return (
      <div className="space-y-5 fade-in">
        <div><div className="text-xs text-muted-foreground font-mono">Step {idx + 1} of {STAGES.length}</div>
          <h2 className="text-2xl font-bold tracking-tight font-display">{meta?.name}</h2>
          <p className="text-sm text-slate-400 mt-0.5">{HELP[stage]?.purpose}</p></div>
        <div data-testid="next-step-card" className="rounded-xl border border-slate-700 bg-slate-900/50 p-5 flex items-center gap-4">
          <Lock size={20} className="text-slate-500" />
          <div className="flex-1"><div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Not yet</div>
            <div className="text-lg font-semibold font-display" data-testid="next-step-title">This step unlocks after {curName}.</div>
            <div className="text-sm text-slate-400">It starts automatically when you get here.</div></div>
          <Button data-testid="locked-go-current" onClick={() => refresh(true)} className="bg-blue-600 hover:bg-blue-500 text-white">Go to {curName}<ArrowRight size={14} className="ml-2" /></Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 fade-in">
      <div className="flex items-end gap-4">
        <div>
          <div className="text-xs text-muted-foreground font-mono">Step {idx + 1} of {STAGES.length}</div>
          <h2 className="text-2xl font-bold tracking-tight font-display">{meta?.name}</h2>
          <p className="text-sm text-slate-400 mt-0.5">{HELP[stage]?.purpose}</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {extra}
          {hasOutput && runs.map((r) => (
            <Button key={r.action} data-testid={r.testid || "stage-rerun-button"} variant="ghost" size="sm" disabled={r.disabled}
              onClick={() => runHook.start(r.action, r.params ? r.params() : {})} className="text-slate-400 hover:text-slate-100">
              <RotateCcw size={13} className="mr-1.5" />{r.label}
            </Button>
          ))}
          {st.status === "approved" && stage !== 10 && !(job.client_approval && stage >= 4) && <Button data-testid="stage-reopen-button" variant="ghost" size="sm" onClick={reopen} className="text-slate-400">Re-open step</Button>}
        </div>
      </div>
      {missing === null ? <div className="rounded-xl border border-border p-5 text-sm text-muted-foreground flex items-center gap-2" data-testid="next-step-loading"><Loader2 size={14} className="animate-spin" />Checking what's needed…</div>
        : <NextStep job={job} stage={stage} st={st} runHook={runHook} runs={runs} missing={missing} approve={approve} goNext={() => refresh(true)} />}
      {st.stale && st.status !== "approved" && <div className="text-xs text-amber-300 flex items-center gap-1.5"><AlertTriangle size={12} /> Something earlier changed, so give this step a quick re-check.</div>}
      <div>
        <button onClick={() => setHelp(!help)} data-testid="how-it-works-toggle" className="text-xs text-slate-400 hover:text-slate-200 inline-flex items-center gap-1">
          <ChevronDown size={13} className={`transition-transform ${help ? "rotate-180" : ""}`} /> How this step works
        </button>
        {help && <ol className="mt-2 ml-1 space-y-1 text-sm text-slate-300 list-decimal pl-5">{HELP[stage]?.steps.map((s) => <li key={s}>{s}</li>)}</ol>}
      </div>
      {runHook?.run && !runHook.busy && <RunStatus run={runHook.run} />}
      {children}
    </div>
  );
}
